import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useServicesContext } from "@/modules/core/services/services.context"
import { useSessionWeekCopy, useSessionWeekCopyActions } from "@/modules/sessions/stores/useSessionWeekCopyStore"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramType } from "@modules/programs/types/programTypes"
import { verboseDateIntervalFormat } from "@modules/utils/dateUtils"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"

export const useProgramSessionWeekDuplicate = () => {
  const queryClient = useQueryClient()
  const weekToCopy = useSessionWeekCopy()
  const { setWeekToCopy, clearWeekToCopy } = useSessionWeekCopyActions()
  const { programInfiniteSessionApi, programOnOffSessionApi } = useServicesContext()
  const language = useLanguage()

  const onCopyInfiniteProgramWeek = (
    programId: string,
    programType: ProgramType.VIRTUAL,
    programName: string,
    originStartDate: string,
    originEndDate: string,
  ) => {
    setWeekToCopy({
      programType,
      originProgramId: programId,
      originProgramName: programName,
      originStartDate,
      originEndDate,
    })

    toast.success(word("program.weekCopyPaste.copy.success"), {
      classNames: successToastClassName(),
    })
  }

  const onPasteInfiniteProgramWeek = async (
    destinationProgramId: string,
    destinationStartDate: string,
    overwrite?: boolean,
  ) => {
    if (weekToCopy?.programType === ProgramType.VIRTUAL) {
      try {
        await programInfiniteSessionApi.copyWeekFromAnInfiniteProgramToAnother(
          weekToCopy.originProgramId,
          destinationProgramId,
          weekToCopy.originStartDate,
          weekToCopy.originEndDate,
          destinationStartDate,
          overwrite ?? false,
        )

        const formattedDates = verboseDateIntervalFormat(
          dayjs(weekToCopy.originStartDate),
          dayjs(weekToCopy.originEndDate),
          language,
        )

        toast.success(word("program.weekCopyPaste.paste.success", { formattedDates }), {
          classNames: successToastClassName(),
        })

        queryClient.invalidateQueries({ queryKey: ["programSessions"] })

        clearWeekToCopy()
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        } else {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: String(e),
            classNames: errorToastClassName(),
          })
        }
      }
    }
  }

  const onCopyOnOffProgramWeek = (
    programId: string,
    programType: ProgramType.ONOFF,
    programName: string,
    originStartIndex: number,
    originEndIndex: number,
  ) => {
    setWeekToCopy({
      programType,
      originProgramId: programId,
      originProgramName: programName,
      originStartIndex,
      originEndIndex,
    })

    toast.success(word("program.weekCopyPaste.copy.success"), {
      classNames: successToastClassName(),
    })
  }

  const onPasteOnOffProgramWeek = async (
    destinationProgramId: string,
    destinationStartIndex: number,
    overwrite?: boolean,
  ) => {
    if (weekToCopy?.programType === ProgramType.ONOFF) {
      try {
        await programOnOffSessionApi.copyWeekFromAnOnOffProgramToAnother(
          weekToCopy.originProgramId,
          destinationProgramId,
          weekToCopy.originStartIndex,
          weekToCopy.originEndIndex,
          destinationStartIndex,
          overwrite ?? false,
        )

        const formattedDates =
          `${word("program.onOff.dayIndex.title")} ${weekToCopy.originStartIndex} - ${word("program.onOff.dayIndex.title")} ${weekToCopy.originEndIndex}`.toLocaleLowerCase()

        toast.success(word("program.weekCopyPaste.paste.success", { formattedDates }), {
          classNames: successToastClassName(),
        })

        queryClient.invalidateQueries({ queryKey: ["programSessions"] })

        clearWeekToCopy()
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        } else {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: String(e),
            classNames: errorToastClassName(),
          })
        }
      }
    }
  }

  const onCopyProProgramWeek = (
    programId: string,
    programType: ProgramType.PRO,
    programName: string,
    originStartDate: string,
    originEndDate: string,
  ) => {
    setWeekToCopy({
      programType,
      originProgramId: programId,
      originProgramName: programName,
      originStartDate,
      originEndDate,
    })

    toast.success(word("program.weekCopyPaste.copy.success"), {
      classNames: successToastClassName(),
    })
  }

  const onPasteProProgramWeek = async (
    destinationProgramId: string,
    destinationStartDate: string,
    overwrite?: boolean,
  ) => {
    if (weekToCopy?.programType === ProgramType.PRO) {
      try {
        await programInfiniteSessionApi.copyWeekFromAnInfiniteProgramToAnother(
          weekToCopy.originProgramId,
          destinationProgramId,
          weekToCopy.originStartDate,
          weekToCopy.originEndDate,
          destinationStartDate,
          overwrite ?? false,
        )

        const formattedDates = verboseDateIntervalFormat(
          dayjs(weekToCopy.originStartDate),
          dayjs(weekToCopy.originEndDate),
          language,
        )

        toast.success(word("program.weekCopyPaste.paste.success", { formattedDates }), {
          classNames: successToastClassName(),
        })

        queryClient.invalidateQueries({ queryKey: ["programSessions"] })

        clearWeekToCopy()
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        } else {
          toast.error(word("program.weekCopyPaste.paste.error"), {
            description: String(e),
            classNames: errorToastClassName(),
          })
        }
      }
    }
  }

  return {
    onCopyInfiniteProgramWeek,
    onPasteInfiniteProgramWeek,
    onCopyOnOffProgramWeek,
    onPasteOnOffProgramWeek,
    onCopyProProgramWeek,
    onPasteProProgramWeek,
    weekToCopy,
  }
}
